import type { UtilityReadingPoint } from '../api/contracts';

/**
 * Pure transforms for UtilityReadingsResponse, kept dependency-free (no
 * apiClient/react-query/auth) for the same reason as costTransforms.ts --
 * unit-testable without a DOM/auth bootstrap. useUtility.ts re-exports these.
 */

export interface UtilityChartPoint {
  x: Date;
  kwhDelivered: number;
  kwhReceived: number;
}

/**
 * Maps raw points to chart points. This site has no solar export
 * (api/src/lib/tariff.ts convention 6: `kwhReceived` is expected to stay ~0
 * throughout), but plots `kwhReceived` exactly as returned -- never assumed
 * or hardcoded to zero.
 */
export function toUtilityChartPoints(points: readonly UtilityReadingPoint[]): UtilityChartPoint[] {
  return points.map((p) => ({
    x: new Date(p.intervalStart),
    kwhDelivered: p.kwhDelivered,
    kwhReceived: p.kwhReceived,
  }));
}

/**
 * True when any reading in the range shows a meaningfully non-zero export.
 * Used to surface a data-quality flag in the UI, not to alter rendering --
 * the chart plots `kwhReceived` unconditionally regardless of this flag.
 */
export function hasUnexpectedExport(points: readonly UtilityReadingPoint[], epsilonKwh = 0.01): boolean {
  return points.some((p) => Math.abs(p.kwhReceived) > epsilonKwh);
}
